<script setup>
import { computed } from 'vue';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName.vue';
import CardLabels from 'dashboard/components/widgets/conversation/conversationCardComponents/CardLabels.vue';
import SLAMissesExpand from './SLAMissesExpand.vue';

const props = defineProps({
  slaName: {
    type: String,
    required: true,
  },
  conversationId: {
    type: Number,
    required: true,
  },
  conversation: {
    type: Object,
    required: true,
  },
  slaEvents: {
    type: Array,
    default: () => [],
  },
});

const AVATAR_PALETTE = [
  { bgClass: 'bg-blue-100', textClass: 'text-blue-700' },
  { bgClass: 'bg-amber-100', textClass: 'text-amber-800' },
  { bgClass: 'bg-red-100', textClass: 'text-red-700' },
  { bgClass: 'bg-emerald-100', textClass: 'text-emerald-700' },
  { bgClass: 'bg-violet-100', textClass: 'text-violet-800' },
];

const conversationLabels = computed(() => {
  const { labels } = props.conversation;
  if (Array.isArray(labels)) {
    return labels;
  }
  return labels ? labels.split(',').map(item => item.trim()) : [];
});

const routerParams = computed(() => ({
  name: 'inbox_conversation',
  params: { conversation_id: props.conversationId },
}));

const contactName = computed(() => props.conversation?.contact?.name || '—');

const conversationReference = computed(() => `#${props.conversationId}`);

const contactInitials = computed(() => {
  const words = contactName.value.split(/\s+/).filter(Boolean).slice(0, 2);
  if (!words.length || contactName.value === '—') {
    return '--';
  }
  return words.map(word => word.charAt(0).toUpperCase()).join('');
});

const unassignedText = '—';

const contactAvatarUrl = computed(
  () => props.conversation?.contact?.avatar_url || null
);

const avatarClass = computed(() => {
  const name = contactName.value;
  let hash = 0;
  let index = 0;
  while (index < name.length) {
    hash = Math.imul(31, hash) + name.charCodeAt(index);
    index += 1;
  }
  const palette = AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
  return [palette.bgClass, palette.textClass];
});
</script>

<template>
  <div
    class="w-full px-6 border-b last:border-b-0 last:rounded-b-xl border-n-weak"
  >
    <div
      class="grid items-center content-center w-full h-16 grid-cols-12 gap-4 py-0"
    >
      <div class="flex items-center gap-3 col-span-6 px-0 py-2 rtl:text-right">
        <img
          v-if="contactAvatarUrl"
          :src="contactAvatarUrl"
          :alt="contactName"
          class="rounded-full size-8 flex-shrink-0 object-cover"
        />
        <span
          v-else
          class="flex items-center justify-center rounded-full size-8 text-xs font-medium flex-shrink-0"
          :class="avatarClass"
        >
          {{ contactInitials }}
        </span>
        <router-link
          :to="routerParams"
          class="flex flex-col min-w-0 hover:underline"
        >
          <span class="text-xs text-n-slate-10">{{
            conversationReference
          }}</span>
          <span class="text-sm font-medium text-n-slate-12 truncate capitalize">
            {{ contactName }}
          </span>
        </router-link>
        <CardLabels
          v-if="conversationLabels.length"
          class="w-[60%]"
          :conversation-id="conversationId"
          :conversation-labels="conversationLabels"
        />
      </div>
      <div
        class="flex items-center capitalize py-2 px-0 text-sm tracking-[0.5] text-n-slate-12 text-left rtl:text-right col-span-3"
      >
        {{ slaName }}
      </div>
      <div class="flex items-center col-span-3 gap-2">
        <UserAvatarWithName
          v-if="conversation.assignee"
          :user="conversation.assignee"
        />
        <span v-else class="text-n-slate-11">{{ unassignedText }}</span>
      </div>
    </div>

    <SLAMissesExpand :sla-events="slaEvents" />
  </div>
</template>
